/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * usePlans
 *
 * Tiny hook around GET /api/billing/plans. Returns `{ plans, loading,
 * error }` with the plans payload mirroring the backend shape:
 *
 *   {
 *     currency, currency_symbol,
 *     monthly: { interval, price, price_per_year },
 *     yearly: { interval, price, effective_monthly,
 *               savings_vs_monthly, months_free_equivalent },
 *     trial_days, minimum_months_monthly,
 *   }
 *
 * v77.99.21 — single source of truth for marketing pages. The page
 * components should render skeletons while `loading` is true (or
 * fall back to last-known cached numbers) rather than hard-coding
 * pricing.
 *
 * Module-level memo: the same plan payload is shared across every
 * mount of the hook to avoid a flicker when navigating between
 * /app/upgrade and /app/upgrade/yearly. Refresh on demand via
 * `refetch()`.
 */
let _cache = null;
let _inflight = null;

export function usePlans() {
  const [plans, setPlans] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  const load = async () => {
    if (_cache) {
      setPlans(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      _inflight = _inflight || api.get("/billing/plans");
      const { data } = await _inflight;
      _cache = data;
      setPlans(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load pricing");
    } finally {
      setLoading(false);
      _inflight = null;
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const refetch = () => {
    _cache = null;
    return load();
  };

  return { plans, loading, error, refetch };
}
